import { useId } from 'react';
import type { PropertyDefinition, PropertyValue } from '@/core/types';
import { useT } from '@/i18n/useT';
import { Input } from '@/ui/components/Input';
import { Select } from '@/ui/components/Select';
import { Toggle } from '@/ui/components/Toggle';

interface PropertyEditorProps {
  readonly definition: PropertyDefinition;
  readonly value: PropertyValue;
  readonly onChange: (key: string, value: PropertyValue) => void;
  readonly disabled?: boolean;
}

/**
 * Renders the editor matching a `PropertyDefinition`. The Properties panel only ever talks to
 * this component, so a new device kind needs a schema — never new panel code.
 */
export function PropertyEditor({
  definition,
  value,
  onChange,
  disabled = false,
}: PropertyEditorProps) {
  const t = useT();
  const id = useId();
  const label = t(definition.labelKey) || definition.labelKey;

  switch (definition.kind) {
    case 'text':
      return (
        <Input
          id={id}
          label={label}
          disabled={disabled}
          value={String(value)}
          {...(definition.maxLength !== undefined ? { maxLength: definition.maxLength } : {})}
          onChange={(event) => onChange(definition.key, event.target.value)}
        />
      );
    case 'number':
      return (
        <Input
          id={id}
          type="number"
          label={label}
          disabled={disabled}
          value={Number(value)}
          {...(definition.min !== undefined ? { min: definition.min } : {})}
          {...(definition.max !== undefined ? { max: definition.max } : {})}
          {...(definition.step !== undefined ? { step: definition.step } : {})}
          {...(definition.unit !== undefined ? { unit: definition.unit } : {})}
          onChange={(event) => {
            const parsed = event.target.valueAsNumber;
            if (!Number.isNaN(parsed)) onChange(definition.key, parsed);
          }}
        />
      );
    case 'boolean':
      return (
        <Toggle
          id={id}
          label={label}
          disabled={disabled}
          checked={value === true}
          onChange={(event) => onChange(definition.key, event.target.checked)}
        />
      );
    case 'select':
      return (
        <Select
          id={id}
          label={label}
          disabled={disabled}
          value={String(value)}
          options={definition.options.map((option) => ({
            value: option.value,
            label: t(option.labelKey) || option.labelKey,
          }))}
          onChange={(event) => onChange(definition.key, event.target.value)}
        />
      );
  }
}
